import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { api } from '../lib/api.js';
import { INDUSTRIES } from '../lib/industries.js';
import { METRICS } from '../lib/choropleth.js';
import { BASEMAPS } from '../lib/basemaps.js';
import { UNIT_OPTIONS } from '../lib/units.js';
import { SETTINGS_EVENT, setPreferences, usePreferences } from '../lib/preferences.js';

// Account menu from the user bubble: four collapsed sections — Profile,
// Teams, Sites, Settings. One section is open at a time; clicking the open
// one collapses it. The menu closes on an outside click, Escape, or
// following a link (handled by TopBar's UserBubble).

// Kept as a re-export: older imports read it from here.
export { SETTINGS_EVENT };
const MAP_FILTER_KEY = 'heatwatch:mapFilters';

export function initials(user) {
  const src = (user?.displayName || user?.email || '?').trim();
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function ProfileTab({ onDone }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(auth.user.displayName ?? '');
  const [msg, setMsg] = useState(null);
  const [pw, setPw] = useState({ current: '', next: '' });
  const [pwMsg, setPwMsg] = useState(null);

  return (
    <div className="acct-section">
      <div className="acct-identity">
        <span className="user-bubble big" aria-hidden="true">
          {initials(auth.user)}
        </span>
        <div>
          <strong>{auth.user.displayName || 'No name set'}</strong>
          <div className="muted">{auth.user.email}</div>
          <div className="muted">
            Member of {auth.teams.length} team{auth.teams.length === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      <form
        className="acct-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setMsg(null);
          try {
            await auth.updateProfile({ displayName: name });
            setMsg({ ok: true, text: 'Saved.' });
          } catch (err) {
            setMsg({ ok: false, text: err.message });
          }
        }}
      >
        <label>
          Display name
          <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
        </label>
        <button className="btn" type="submit" disabled={name === (auth.user.displayName ?? '')}>
          Save name
        </button>
        {msg && <span className={msg.ok ? 'acct-ok' : 'acct-err'}>{msg.text}</span>}
      </form>

      <form
        className="acct-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setPwMsg(null);
          try {
            await api('/auth/password', { method: 'POST', body: { currentPassword: pw.current, newPassword: pw.next } });
            setPw({ current: '', next: '' });
            setPwMsg({ ok: true, text: 'Password changed. Other devices were signed out.' });
          } catch (err) {
            setPwMsg({ ok: false, text: err.message });
          }
        }}
      >
        <label>
          Current password
          <input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
        </label>
        <label>
          New password <span className="muted">(10+ characters)</span>
          <input type="password" autoComplete="new-password" minLength={10} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
        </label>
        <button className="btn" type="submit" disabled={!pw.current || pw.next.length < 10}>
          Change password
        </button>
        {pwMsg && <span className={pwMsg.ok ? 'acct-ok' : 'acct-err'}>{pwMsg.text}</span>}
      </form>

      <button
        type="button"
        className="btn danger acct-signout"
        onClick={async () => {
          onDone();
          await auth.logout();
          navigate('/');
        }}
      >
        Sign out
      </button>
    </div>
  );
}

export function TeamsTab({ onDone }) {
  const auth = useAuth();
  const [code, setCode] = useState('');
  const [newTeam, setNewTeam] = useState('');
  const [msg, setMsg] = useState(null);
  const run = (fn) => async (e) => {
    e.preventDefault();
    setMsg(null);
    try {
      await fn();
      await auth.refresh();
    } catch (err) {
      setMsg(err.message);
    }
  };
  return (
    <div className="acct-section">
      <ul className="acct-list">
        {auth.teams.map((t) => (
          <li key={t.id}>
            <Link to={`/teams/${t.id}`} onClick={onDone}>
              <strong>{t.name}</strong>
              <span className="muted">
                {t.role} · {t.memberCount} member{t.memberCount === 1 ? '' : 's'} · {t.siteCount} site
                {t.siteCount === 1 ? '' : 's'}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <form className="acct-form inline" onSubmit={run(async () => {
        const t = await api('/teams/join', { method: 'POST', body: { inviteCode: code } });
        setCode('');
        setMsg(`Joined ${t.name}.`);
      })}>
        <label>
          Join with an invite code
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="K7M2P9QXRT" />
        </label>
        <button className="btn" type="submit" disabled={!code.trim()}>
          Join
        </button>
      </form>
      <form className="acct-form inline" onSubmit={run(async () => {
        await api('/teams', { method: 'POST', body: { name: newTeam } });
        setNewTeam('');
      })}>
        <label>
          Create a team
          <input value={newTeam} maxLength={100} onChange={(e) => setNewTeam(e.target.value)} placeholder="Team name" />
        </label>
        <button className="btn" type="submit" disabled={!newTeam.trim()}>
          Create
        </button>
      </form>
      {msg && <div className="acct-note">{msg}</div>}
      <p className="muted acct-note">Open a team to manage its members, roles and invite code.</p>
    </div>
  );
}

export function SitesTab({ onDone }) {
  const auth = useAuth();
  const [byTeam, setByTeam] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all(auth.teams.map((t) => api(`/teams/${t.id}/sites`).then((sites) => ({ team: t, sites })).catch(() => ({ team: t, sites: [], error: true }))))
      .then((r) => !cancelled && setByTeam(r));
    return () => {
      cancelled = true;
    };
  }, [auth.teams]);

  if (!byTeam) return <div className="acct-section muted">Loading sites…</div>;
  const total = byTeam.reduce((n, g) => n + g.sites.length, 0);
  return (
    <div className="acct-section">
      {total === 0 && <p className="muted">No saved sites yet.</p>}
      {byTeam
        .filter((g) => g.sites.length || g.error)
        .map((g) => (
          <div key={g.team.id}>
            <div className="acct-group">{g.team.name}</div>
            {g.error && <div className="acct-err">Couldn’t load this team’s sites.</div>}
            <ul className="acct-list">
              {g.sites.map((s) => (
                <li key={s.id}>
                  <Link to={`/sites/${s.id}`} onClick={onDone}>
                    <strong>{s.name}</strong>
                    <span className="muted">
                      {s.municipality ?? s.countyName ?? '—'}, {s.state}
                    </span>
                    {s.activeIncidents > 0 && <span className="chip chip-official">{s.activeIncidents} active</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      <Link className="btn" to="/sites" onClick={onDone}>
        {total ? 'Manage sites' : 'Add a site'}
      </Link>
      {auth.teams.some((t)=>['owner','admin'].includes(t.role)) && <Link className="btn primary" to="/sites?upload=1" onClick={onDone}>Upload Data</Link>}
      <Link className="btn" to="/sites?uploads=1" onClick={onDone}>See uploads</Link>
    </div>
  );
}

const UNIT_FIELDS = [
  ['temperature', 'Temperature'],
  ['speed', 'Wind speed'],
  ['precipitation', 'Precipitation'],
];

export function SettingsTab() {
  const prefs = usePreferences();
  const [note, setNote] = useState(null);
  const save = (patch) => {
    setNote(null);
    setPreferences(patch).catch((err) => setNote({ ok: false, text: `Saved in this browser only: ${err.message}` }));
  };

  return (
    <div className="acct-section">
      <fieldset className="acct-fieldset">
        <legend>Units</legend>
        {UNIT_FIELDS.map(([kind, label]) => (
          <label key={kind} className="acct-field acct-field-row">
            {label}
            <select value={prefs.units[kind]} onChange={(e) => save({ units: { [kind]: e.target.value } })}>
              {UNIT_OPTIONS[kind].map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </select>
          </label>
        ))}
        <span className="muted">
          Display only. Regulatory thresholds quoted in rule text stay in the units the rule is written in.
        </span>
      </fieldset>

      <fieldset className="acct-fieldset">
        <legend>Map</legend>
        <label className="acct-field acct-field-row">
          Map style
          <select value={prefs.map.basemap} onChange={(e) => save({ map: { basemap: e.target.value } })}>
            {BASEMAPS.map((b) => (
              <option key={b.key} value={b.key}>
                {b.label}
              </option>
            ))}
          </select>
        </label>
        <label className="acct-field acct-field-row">
          Colour counties by
          <select value={prefs.map.metric} onChange={(e) => save({ map: { metric: e.target.value } })}>
            {Object.entries(METRICS).map(([key, m]) => (
              <option key={key} value={key}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn"
          onClick={() => {
            save({ map: { metric: 'severity' } });
            try {
              localStorage.removeItem(MAP_FILTER_KEY);
            } catch {
              /* storage blocked */
            }
            window.dispatchEvent(new CustomEvent(SETTINGS_EVENT, { detail: { reset: true } }));
            setNote({ ok: true, text: 'Map filters reset.' });
          }}
        >
          Reset map filters
        </button>
      </fieldset>

      <fieldset className="acct-fieldset">
        <legend>Work rules</legend>
        <label className="acct-field">
          Default industry rules
          <select value={prefs.industry} onChange={(e) => save({ industry: e.target.value })}>
            {INDUSTRIES.map((i) => (
              <option key={i.key} value={i.key}>
                {i.label}
              </option>
            ))}
          </select>
          <span className="muted">Used only for the calculated rule triggers on ZIP conditions.</span>
        </label>
      </fieldset>

      {note && <span className={note.ok ? 'acct-ok' : 'acct-err'}>{note.text}</span>}
      <p className="muted acct-note">Saved to your account, so they follow you to other devices.</p>
    </div>
  );
}

// 18 px line icons (currentColor), so they follow the theme.
const Icon = ({ children }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const ICONS = {
  profile: (
    <Icon>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
    </Icon>
  ),
  teams: (
    <Icon>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
      <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14.3c2.1.7 3.5 2.8 3.5 5.7" />
    </Icon>
  ),
  sites: (
    <Icon>
      <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </Icon>
  ),
  settings: (
    <Icon>
      <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="10" cy="12" r="2" />
      <circle cx="18" cy="18" r="2" />
    </Icon>
  ),
};

export const SECTIONS = [
  { key: 'profile', label: 'Profile', Body: ProfileTab, path: '/account' },
  { key: 'teams', label: 'Teams', Body: TeamsTab, path: '/teams' },
  { key: 'sites', label: 'Sites', Body: SitesTab, path: '/sites' },
  { key: 'settings', label: 'Settings', Body: SettingsTab, path: '/settings' },
];

/**
 * The menu itself: four collapsed rows. Clicking a row expands its content
 * (and collapses any other); clicking the open row collapses it. `onDone`
 * closes the whole menu.
 */
export default function AccountPanel({ onDone }) {
  const [open, setOpen] = useState(null);
  return (
    <nav className="account-panel" aria-label="Account">
      <ul className="acct-accordion">
        {SECTIONS.map(({ key, label, Body, path }) => {
          const isOpen = open === key;
          return (
            <li key={key} className={`acct-item${isOpen ? ' open' : ''}`}>
              <button
                type="button"
                id={`acct-head-${key}`}
                className="acct-row"
                aria-expanded={isOpen}
                aria-controls={`acct-body-${key}`}
                onClick={() => setOpen(isOpen ? null : key)}
              >
                <span className="acct-icon">{ICONS[key]}</span>
                <span className="acct-label">{label}</span>
                <svg className="acct-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
              {/* Expand: the same section as a full page (direct link, browser history). */}
              <Link className="acct-expand" to={path} onClick={onDone} aria-label={`Expand ${label}`} title={`Open ${label} page`}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" />
                </svg>
              </Link>
              {isOpen && (
                <div id={`acct-body-${key}`} role="region" aria-labelledby={`acct-head-${key}`} className="acct-body">
                  <Body onDone={onDone} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
